import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as TrustedShopChevronLeft } from '../assets/trusted-shop-chavron-left.svg';
import { useTrustedShopsReviews } from './hooks/useTrustedShopsReviews';
import { TrustedShopsGradeTile } from './TrustedShopsGradeTile';
import { TrustedShopsReviewCard } from './TrustedShopsReviewCard';
import styles from './TrustedShopsWidget.module.scss';
import type { TrustedShopsWidgetProps } from './types';

const SCROLL_THRESHOLD = 10;

const TrustedShopsWidget = ({ rating }: TrustedShopsWidgetProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const reviews = useTrustedShopsReviews();

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const syncPadding = () => {
      updateScrollState();
    };

    const updateScrollState = () => {
      requestAnimationFrame(() => {
        const current = scrollRef.current;
        if (!current) return;
        const { scrollLeft, scrollWidth, clientWidth } = current;
        const contentEndScrollLeft = scrollWidth - clientWidth;
        const atRightEnd =
          contentEndScrollLeft <= 0 || scrollLeft >= contentEndScrollLeft - 1;
        setScrollState({
          canScrollLeft: scrollLeft > SCROLL_THRESHOLD,
          canScrollRight: !atRightEnd,
        });
      });
    };

    syncPadding();
    window.addEventListener('resize', syncPadding);
    el.addEventListener('scroll', updateScrollState);
    el.addEventListener('scrollend', updateScrollState);

    return () => {
      window.removeEventListener('resize', syncPadding);
      el.removeEventListener('scroll', updateScrollState);
      el.removeEventListener('scrollend', updateScrollState);
    };
  }, [reviews.data]);

  const handleScroll = useCallback((direction: 'left' | 'right') => {
    const container = scrollRef.current;
    if (!container) return;

    const wrapper = container.parentElement;
    const viewportWidth = wrapper?.clientWidth ?? container.clientWidth;

    const containerRect = container.getBoundingClientRect();
    const children = Array.from(container.children) as HTMLElement[];
    const isRight = direction === 'right';
    const candidates = isRight ? children : [...children].reverse();

    const target = candidates.find((child) => {
      const r = child.getBoundingClientRect();
      return isRight
        ? r.right > containerRect.right
        : r.left < containerRect.left;
    });

    if (target) {
      const scrollLeft =
        direction === 'right'
          ? target.offsetLeft
          : Math.max(0, target.offsetLeft + target.offsetWidth - viewportWidth);
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    } else {
      const delta = (isRight ? 1 : -1) * viewportWidth;
      container.scrollBy({ left: delta, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className={styles.widgetContainer}>
      <TrustedShopsGradeTile
        rating={rating}
        className={styles.gradeTileDesktop}
      />

      <button
        className={styles.navButton}
        onClick={() => handleScroll('left')}
        aria-label="Vorherige Bewertung"
        disabled={!scrollState.canScrollLeft}
      >
        <TrustedShopChevronLeft aria-hidden="true" />
      </button>

      <div
        className={clsx(styles.carouselWrapper, {
          [styles.maskRight]: scrollState.canScrollRight,
          [styles.maskLeft]: scrollState.canScrollLeft,
        })}
        role="region"
        aria-label="Kundenbewertungen"
        aria-busy={reviews.isPending}
      >
        <div className={styles.scrollContainer} ref={scrollRef} tabIndex={-1}>
          <TrustedShopsGradeTile
            rating={rating}
            className={styles.gradeTileMobile}
          />
          {reviews.isPending && !reviews.data ? (
            <span>Laden…</span>
          ) : (
            reviews.data?.map((review) => (
              <TrustedShopsReviewCard key={review.id} review={review} />
            ))
          )}
        </div>
      </div>

      <button
        className={`${styles.navButton} ${styles.navButtonRight}`}
        onClick={() => handleScroll('right')}
        aria-label="Nächste Bewertung"
        disabled={!scrollState.canScrollRight}
      >
        <TrustedShopChevronLeft aria-hidden="true" />
      </button>
    </div>
  );
};

export default TrustedShopsWidget;
